/**
 * TextareaField -- Dynamic multi-line text input field.
 *
 * Renders a shadcn/ui Textarea component with an optional character count
 * display when `max_length` is defined in the field schema. Supports label,
 * required indicator, placeholder, help text, tooltip, validation error
 * display, and full ARIA accessibility.
 */

import { HelpCircle } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { DynamicFieldProps } from './index';

export function TextareaField({ field, value, onChange, error, readOnly }: DynamicFieldProps) {
  const textareaId = `field-${field.name}`;
  const helpId = `${textareaId}-help`;
  const errorId = `${textareaId}-error`;
  const countId = `${textareaId}-count`;

  const currentValue = typeof value === 'string' ? value : '';
  const charCount = currentValue.length;
  const maxLength = field.max_length ?? undefined;

  const describedBy = [
    field.help_text ? helpId : null,
    maxLength ? countId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label htmlFor={textareaId}>
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="text-destructive ml-1">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>

        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <Textarea
        id={textareaId}
        value={currentValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder ?? undefined}
        disabled={readOnly}
        maxLength={maxLength}
        aria-required={field.required}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={cn(error && 'border-destructive focus-visible:ring-destructive')}
      />

      {maxLength && (
        <p
          id={countId}
          className={cn(
            'text-xs text-right',
            charCount >= maxLength ? 'text-destructive' : 'text-muted-foreground'
          )}
          aria-live="polite"
        >
          {charCount}/{maxLength} characters
        </p>
      )}

      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
